import React from 'react';
import LegalPageLayout, { LegalSection } from '../components/LegalPageLayout';

const TermsPage = () => {
  return (
    <LegalPageLayout
      testId="terms-page"
      eyebrow="Legal"
      title="Terms of Service"
      lastUpdated="February 2026"
    >
      <LegalSection number="1" title="About these terms">
        <p>
          These are the platform terms of Sebastian Alliance Group LLC ("SAG",
          "we", "us"), a Florida limited liability company operating digital
          commerce, payment orchestration and financial technology platforms
          from Fort Lauderdale, Florida. By accessing our websites, portals or
          Payment Center, or by submitting a payment or form on any SAG
          property, you agree to these terms.
        </p>
        <p>
          These terms cover SAG's role as the technology provider. They are{' '}
          <strong>not</strong> the terms of any merchant, client, portfolio
          company or third-party service you may transact with through our
          platforms — those parties publish their own terms, refund and privacy
          policies.
        </p>
      </LegalSection>

      <LegalSection number="2" title="Our role">
        <p>
          SAG provides payment technology, secure hosting, digital storefronts,
          business-formation tools, real-estate workflow software and related
          professional services. Card and bank payments accepted through the
          SAG Payment Center are processed by regulated third parties including
          Stripe, Inc. and Fiserv, Inc. Funds settle to the receiving merchant
          or SAG operating account through those processors and their banking
          partners in accordance with applicable card-scheme and banking rules.
        </p>
        <p>
          SAG is not a bank, broker-dealer, money transmitter or investment
          adviser. Where a service on our platform is delivered by a licensed
          third party (for example, banking partners or financing providers),
          that party's own terms and regulator oversight apply.
        </p>
      </LegalSection>

      <LegalSection number="3" title="Your payment">
        <p>
          Amounts, currency, item descriptions and receipts displayed at
          checkout are supplied by the SAG entity or client raising the
          invoice. Card details are captured on the processor's PCI-DSS
          compliant hosted payment page. SAG does not store full card numbers,
          CVV values, expiration dates or online banking credentials.
        </p>
      </LegalSection>

      <LegalSection number="4" title="Accounts and eligibility">
        <p>
          Some areas of our platform (Admin portal, Investor Portal, Merchant
          dashboards) require an account. You must be at least 18 years old
          and legally able to enter into a binding contract. You are
          responsible for keeping your credentials confidential and for all
          activity on your account. Notify us immediately at{' '}
          <a href="mailto:ecommerce@sebasalliance.net" className="text-[#C9A227] hover:underline">
            ecommerce@sebasalliance.net
          </a>{' '}
          if you suspect unauthorised use.
        </p>
      </LegalSection>

      <LegalSection number="5" title="Acceptable use">
        <p>
          You may not use SAG platforms for unlawful, fraudulent or prohibited
          activity, to launder proceeds of crime, to fund terrorism, to breach
          US or applicable sanctions (including OFAC), or to attempt
          unauthorised access to any system. We may decline, hold, reverse or
          refund transactions and suspend accounts that appear to breach these
          terms, our KYC/AML Policy, card-scheme rules or applicable law.
        </p>
      </LegalSection>

      <LegalSection number="6" title="Refunds, disputes and chargebacks">
        <p>
          Refund and cancellation requests are handled under our{' '}
          <a href="/refund-policy" className="text-[#C9A227] hover:underline">
            Return &amp; Refund Policy
          </a>
          . If you do not recognise a charge on your card statement, please
          contact us first — most issues are resolved faster directly than
          through a chargeback. Fraudulent chargebacks may be contested with
          the acquiring bank and card networks using transaction, device and
          verification evidence collected at checkout.
        </p>
      </LegalSection>

      <LegalSection number="7" title="Intellectual property">
        <p>
          All SAG trademarks, service marks, logos, page designs, software,
          documentation and portfolio images are the property of Sebastian
          Alliance Group LLC or its licensors. You are granted a limited,
          non-exclusive, non-transferable licence to use our platforms for
          their intended purpose. You may not copy, reverse-engineer,
          redistribute or create derivative works without our prior written
          consent.
        </p>
      </LegalSection>

      <LegalSection number="8" title="Security">
        <p>
          Payment pages are served over HTTPS/TLS. Admin credentials are hashed
          with industry-standard algorithms. Sensitive configuration such as
          processor API keys are encrypted at rest using authenticated
          encryption before being written to our datastore, and access is
          restricted to authorised administrators through JWT-based sessions
          with audit logging.
        </p>
      </LegalSection>

      <LegalSection number="9" title="Third-party links and services">
        <p>
          Our platforms may link to or embed third-party services (for example
          Stripe checkout, Fiserv gateways, Sekur secure email, Google Maps,
          analytics providers). Your use of those services is governed by
          their own terms and privacy policies. SAG is not responsible for
          third-party content, availability or practices.
        </p>
      </LegalSection>

      <LegalSection number="10" title="Disclaimers">
        <p>
          To the maximum extent permitted by law, our platforms and services
          are provided on an "as-is" and "as-available" basis. SAG disclaims
          all implied warranties, including merchantability, fitness for a
          particular purpose and non-infringement. We do not warrant that the
          platforms will be uninterrupted, error-free or free of harmful
          components.
        </p>
      </LegalSection>

      <LegalSection number="11" title="Limitation of liability">
        <p>
          To the extent permitted by law, SAG, its affiliates, officers,
          employees and agents will not be liable for indirect, incidental,
          special, consequential, exemplary or punitive damages, or for any
          loss of profits, revenue, data or goodwill, arising out of or
          related to the platforms — even if advised of the possibility of
          such damages. Our aggregate liability for any claim will not exceed
          the greater of (a) the fees you paid to SAG in the twelve (12)
          months preceding the claim or (b) US $100. Nothing in these terms
          excludes liability that cannot lawfully be excluded.
        </p>
      </LegalSection>

      <LegalSection number="12" title="Indemnity">
        <p>
          You agree to indemnify and hold harmless SAG and its affiliates
          from any claim, demand, loss or expense (including reasonable
          attorneys' fees) arising out of your breach of these terms, your
          misuse of the platforms, or your violation of any law or the rights
          of a third party.
        </p>
      </LegalSection>

      <LegalSection number="13" title="Changes and termination">
        <p>
          We may update these terms; the version shown at the time of your
          transaction or use applies to that transaction. We may suspend or
          terminate access to the platforms at any time for breach of these
          terms, suspected fraud, regulatory or legal reasons.
        </p>
      </LegalSection>

      <LegalSection number="14" title="Governing law and disputes">
        <p>
          These terms are governed by the laws of the State of Florida, United
          States, without regard to conflict-of-laws principles. Any dispute
          arising out of or in connection with these terms shall be resolved
          in the state or federal courts located in Broward County, Florida,
          and you consent to their exclusive jurisdiction and venue, unless
          binding arbitration is required by applicable law.
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
};

export default TermsPage;
